/**
 * DICOM Helper Functions
 *
 * Utilities for parsing patient data from DICOM files
 */

import type { DocumentType } from '@/config/constants'

/**
 * Parse DICOM Patient Name (0010,0010)
 * Format: LastName^FirstName^MiddleName^Prefix^Suffix
 */
export function parseDicomPatientName(dicomName: string | undefined): {
  firstName: string | null
  lastName: string | null
  middleName: string | null
} {
  if (!dicomName) return { firstName: null, lastName: null, middleName: null }

  const parts = dicomName.split('^')
  return {
    lastName: parts[0]?.trim() || null,
    firstName: parts[1]?.trim() || null,
    middleName: parts[2]?.trim() || null,
  }
}

/**
 * Parse document number from DICOM Patient ID
 * Handles prefixes like "CC-12345678", "TI12345678", "CC 12345678"
 */
export function parseDocumentFromPatientId(patientId: string | undefined): {
  documentType: DocumentType
  documentNumber: string
} {
  if (!patientId) return { documentType: 'CC', documentNumber: '' }

  const trimmed = patientId.trim()

  const prefixPatterns: [RegExp, DocumentType][] = [
    [/^CC[-\s]?/i, 'CC'],
    [/^TI[-\s]?/i, 'TI'],
    [/^CE[-\s]?/i, 'CE'],
    [/^PA[-\s]?/i, 'PA'],
    [/^RC[-\s]?/i, 'RC'],
    [/^NV[-\s]?/i, 'NV'],
    [/^PE[-\s]?/i, 'PE'],
    [/^MS[-\s]?/i, 'MS'],
    [/^AS[-\s]?/i, 'AS'],
    [/^CD[-\s]?/i, 'CD'],
    [/^SC[-\s]?/i, 'SC'],
  ]

  for (const [pattern, type] of prefixPatterns) {
    if (pattern.test(trimmed)) {
      return {
        documentType: type,
        documentNumber: trimmed.replace(pattern, '').trim(),
      }
    }
  }

  return { documentType: 'CC', documentNumber: trimmed }
}

/**
 * Normalize DICOM sex to M/F/O
 */
export function normalizeDicomSex(sex: string | undefined): 'M' | 'F' | 'O' {
  if (!sex) return 'O'
  const upper = sex.toUpperCase().trim()
  if (upper === 'M' || upper === 'MALE') return 'M'
  if (upper === 'F' || upper === 'FEMALE') return 'F'
  return 'O'
}

/**
 * Parse DICOM date format (YYYYMMDD) to ISO date (YYYY-MM-DD)
 */
export function parseDicomDate(dicomDate: string | undefined): string | null {
  if (!dicomDate || dicomDate.length < 8) return null

  // Already in ISO format
  if (dicomDate.includes('-')) return dicomDate

  // YYYYMMDD format
  const year = dicomDate.slice(0, 4)
  const month = dicomDate.slice(4, 6)
  const day = dicomDate.slice(6, 8)

  // Validate
  const date = new Date(`${year}-${month}-${day}`)
  if (isNaN(date.getTime())) return null

  return `${year}-${month}-${day}`
}

/**
 * Patient data structure for form
 */
export interface PatientCreateData {
  documentNumber: string
  documentType: DocumentType
  firstName: string
  lastName: string
  middleName?: string
  secondLastName?: string
  birthDate?: string
  sex: 'M' | 'F' | 'O'
}

/**
 * Extract patient data from DICOM metadata
 */
export function extractPatientDataFromDicom(metadata: {
  patientId?: string
  patientName?: string
  patientBirthDate?: string
  patientSex?: string
}): PatientCreateData {
  const { documentType, documentNumber } = parseDocumentFromPatientId(metadata.patientId)
  const { firstName, lastName, middleName } = parseDicomPatientName(metadata.patientName)
  const birthDate = parseDicomDate(metadata.patientBirthDate)
  const sex = normalizeDicomSex(metadata.patientSex)

  return {
    documentType,
    documentNumber,
    firstName: firstName || '',
    lastName: lastName || '',
    middleName: middleName || undefined,
    birthDate: birthDate || undefined,
    sex,
  }
}

/**
 * Validate patient data has minimum required fields
 */
export function isValidPatientData(data: PatientCreateData): boolean {
  return !!(data.documentNumber || data.lastName)
}
